import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import { useCartStore, useLocaleStore } from '@/lib/core/stores';
import { cn, LinkRouteUrl, shareContent, toShareUrl } from '@/lib/core/utils';
import { AppContext } from '@/lib/core/types/modules';
import type { ProductDetailDto } from '@/services/platform/api/model';
import { getActiveShopSectionSlug } from '@/services/platform/data/shop/catalog';
import { Icon } from '@/components/ui/icon';

interface ShopHeaderActionsProps {
  context?: AppContext;
  /** Hides the search action — used on screens that don't offer search (e.g. product list). */
  hideSearch?: boolean;
  /** Current product list view; when provided (with `onViewChange`) renders the view-toggle button. */
  view?: number;
  onViewChange?: () => void;
  /** Whether the header sits over a dark hero image — controls icon/text colour. Defaults to true (dark backdrop). */
  showHeroCarousel?: boolean;
  /** Shows a native-share action (via `navigator.share`) for the current page URL — e.g. product detail. */
  showShare?: boolean;
  /** Product being shared — its title is used as the share title. */
  product?: ProductDetailDto | null;
}

export function ShopHeaderActions({
  context,
  hideSearch,
  view,
  onViewChange,
  showHeroCarousel = false,
  showShare,
  product,
}: ShopHeaderActionsProps) {
  const t = useClientTranslation();
  const location = useLocation();
  const locale = useLocaleStore(state => state.locale);
  const itemCount = useCartStore(state => state.itemCount);

  const shopSearch = context?.settings?.properties?.shopSearch;
  const shopShoppingBag = context?.settings?.properties?.shopShoppingBag;

  const searchBaseHref = LinkRouteUrl(shopSearch?.route) ?? undefined;
  const shoppingBagHref = LinkRouteUrl(shopShoppingBag?.route) ?? undefined;

  const textColor = showHeroCarousel ? 'text-white' : 'text-jncquoi-neutral-1000';

  const [activeSectionSlug, setActiveSectionSlug] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (hideSearch || !searchBaseHref) return;
    let isCurrent = true;
    getActiveShopSectionSlug(context?.settings, location.pathname, locale).then(slug => {
      if (isCurrent) setActiveSectionSlug(slug);
    });
    return () => {
      isCurrent = false;
    };
  }, [hideSearch, searchBaseHref, context?.settings, location.pathname, locale]);

  const searchHref = searchBaseHref
    ? activeSectionSlug
      ? `${searchBaseHref}${searchBaseHref.includes('?') ? '&' : '?'}section=${encodeURIComponent(activeSectionSlug)}`
      : searchBaseHref
    : undefined;

  return (
    <div className="flex items-center gap-1">
      {view !== undefined && onViewChange && (
        <button type="button" className="flex h-11 items-center justify-center px-2" onClick={onViewChange}>
          <span
            className={cn(
              textColor,
              'font-pp-neue-montreal text-[12px] leading-none tracking-[0.5px] uppercase'
            )}
          >
            {t('App.Shop.View', { view })}
          </span>
        </button>
      )}
      {showShare && (
        <button
          type="button"
          aria-label={t('App.Labels.Share')}
          className="flex size-11 items-center justify-center"
          onClick={() => shareContent({ title: product?.title ?? '', url: toShareUrl(window.location.href) })}
        >
          <Icon name="share-ios" width={24} height={24} strokeWidth={1.5} className={textColor} />
        </button>
      )}
      {shoppingBagHref && (
        <Link
          to={shoppingBagHref}
          aria-label={t('App.Labels.ShoppingBag')}
          className="flex h-11 items-center justify-center gap-1 px-2"
        >
          {itemCount > 0 && (
            <span className={cn(textColor, 'font-pp-neue-montreal text-[12px] leading-none font-semibold')}>
              {itemCount}
            </span>
          )}
          <Icon name="shopping-bag" width={24} height={24} strokeWidth={1.5} className={textColor} />
        </Link>
      )}
      {!hideSearch && searchHref && (
        <Link
          to={searchHref}
          aria-label={t('App.Labels.Search')}
          className="flex size-11 items-center justify-center"
        >
          <Icon name="search" width={24} height={24} strokeWidth={1.5} className={textColor} />
        </Link>
      )}
    </div>
  );
}
